import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { fetchAssignedWorkoutTree, type AssignedWorkoutTree } from "../lib/assignedWorkoutTree";
import type { QueueRow } from "../lib/reviewQueue";

/* THE REVIEW WRITE'S READS (W82 step 4) — for ONE queue row, through RLS as the signed-in coach:
 *   • what was already SENT about this workout — the `review` and `comment` rows naming its session (the two parties
 *     read; migration `20260926100000` added the columns under the SELECT policy that already existed);
 *   • THIS week's copy — the day's ONE training assignment (0 or 2+ → no next week is offered: the queue's own rule);
 *   • NEXT week's copy — the first later assignment of the SAME template (`coalesce(root_workout_id, workout_id)`, the
 *     overlay's and the unique index's identity), both trees through `assigned_workout_tree` (the overlay's reader, with
 *     the set ids the write names).
 * A failed read is a failed card, never an empty one: "nothing written for next week" would then be a lie. */

export type SentRow = {
  id: string; kind: string; body: string; sessionId: string | null; exerciseRowId: string | null; createdAtISO: string;
  readAtISO: string | null; replyTo: string | null; senderId: string;
  /** Step 5 — the row's takes (`coach_messages.audio_*` / `video_*`); the objects may since have been removed. */
  audioPath: string | null; audioSeconds: number | null; videoPath: string | null; videoSeconds: number | null;
  /** 2026-10-07 — the row's photo (`coach_messages.photo_path`). */
  photoPath: string | null;
};
export type WriteState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      review: SentRow | null;
      comments: SentRow[];
      /** Every row of this client's conversation — the replies rooted at a comment are read out of it (`threadOf`). */
      thread: SentRow[];
      thisTree: AssignedWorkoutTree | null;
      nextTree: AssignedWorkoutTree | null;
      nextDay: string | null;
      day: string | null;
      /** N2d — THIS week's copy (`coach_assignments.workout_id`), what `Repeat next week` clones; null = no single copy. */
      thisWorkoutId: string | null;
      /** NEXT week's copy (`coach_assignments.workout_id` of its day) — what `Edit next week` opens in the builder. */
      nextWorkoutId: string | null;
    };

type AssignRow = { workout_id: string; root_workout_id: string | null; scheduled_date: string | null; coach_workouts: { kind: string | null } | { kind: string | null }[] | null };
const kindOf = (a: AssignRow) => (Array.isArray(a.coach_workouts) ? a.coach_workouts[0]?.kind : a.coach_workouts?.kind) ?? null;
const rpc = (fn: "assigned_workout_tree", args: { p_workout_id: string }) => supabase.rpc(fn, args);

export function useReviewWrite(row: QueueRow | null): { state: WriteState; reload: () => void } {
  const [state, setState] = useState<WriteState>({ status: "loading" });
  const [seq, setSeq] = useState(0);
  const reload = useCallback(() => setSeq((n) => n + 1), []);
  useEffect(() => {
    if (row === null) return;
    let cancelled = false;
    void (async () => {
      const fail = (message: string) => { if (!cancelled) setState({ status: "error", message }); };
      // The whole conversation with this client: the review and its comments name the session; the replies under a
      // comment name only what they quote (`reply_to`), so they are found by their root, never by a column.
      const sent = await supabase.from("coach_messages")
        .select("id, kind, body, session_id, exercise_row_id, created_at, read_at, reply_to, sender_id, audio_path, audio_seconds, video_path, video_seconds, photo_path")
        .eq("client_id", row.clientId);
      if (cancelled) return;
      if (sent.error !== null) return fail(sent.error.message);
      const all: SentRow[] = (sent.data ?? []).map((m) => ({
        id: m.id, kind: m.kind, body: m.body, sessionId: m.session_id, exerciseRowId: m.exercise_row_id, createdAtISO: m.created_at,
        readAtISO: m.read_at, replyTo: m.reply_to, senderId: m.sender_id,
        audioPath: m.audio_path, audioSeconds: m.audio_seconds, videoPath: m.video_path, videoSeconds: m.video_seconds, photoPath: m.photo_path,
      }));
      const rows = all.filter((m) => m.sessionId === row.sessionId && (m.kind === "review" || m.kind === "comment"));

      const one = await supabase.from("workout_sessions").select("scheduled_date").eq("id", row.sessionId).maybeSingle();
      if (cancelled) return;
      if (one.error !== null) return fail(one.error.message);
      const day = one.data?.scheduled_date?.slice(0, 10) ?? null;

      let thisTree: AssignedWorkoutTree | null = null;
      let nextTree: AssignedWorkoutTree | null = null;
      let nextDay: string | null = null;
      let thisWorkoutId: string | null = null;
      let nextWorkoutId: string | null = null;
      if (day !== null) {
        const a = await supabase.from("coach_assignments")
          .select("workout_id, root_workout_id, scheduled_date, coach_workouts!workout_id(kind)")
          .eq("client_id", row.clientId).eq("scheduled_date", day);
        if (cancelled) return;
        if (a.error !== null) return fail(a.error.message);
        const training = ((a.data ?? []) as unknown as AssignRow[]).filter((x) => kindOf(x) !== "task");
        const mine = training.length === 1 ? training[0] : undefined;
        if (mine !== undefined) {
          const source = mine.root_workout_id ?? mine.workout_id;
          const later = await supabase.from("coach_assignments")
            .select("workout_id, root_workout_id, scheduled_date, coach_workouts!workout_id(kind)")
            .eq("client_id", row.clientId).gt("scheduled_date", day)
            .or(`root_workout_id.eq.${source},workout_id.eq.${source}`)
            .order("scheduled_date", { ascending: true }).limit(1);
          if (cancelled) return;
          if (later.error !== null) return fail(later.error.message);
          const next = ((later.data ?? []) as unknown as AssignRow[]).find((x) => kindOf(x) !== "task");
          const [t1, t2] = await Promise.all([
            fetchAssignedWorkoutTree(rpc, mine.workout_id),
            next === undefined ? Promise.resolve(null) : fetchAssignedWorkoutTree(rpc, next.workout_id),
          ]);
          if (cancelled) return;
          if (t1.tree === null) return fail(t1.error);
          if (t2 !== null && t2.tree === null) return fail(t2.error);
          thisTree = t1.tree;
          nextTree = t2?.tree ?? null;
          nextDay = next?.scheduled_date?.slice(0, 10) ?? null;
          thisWorkoutId = mine.workout_id;
          nextWorkoutId = next?.workout_id ?? null;
        }
      }
      if (!cancelled) setState({
        status: "ready",
        review: rows.find((r) => r.kind === "review") ?? null,
        comments: rows.filter((r) => r.kind === "comment"),
        thread: all,
        thisTree, nextTree, nextDay, day, thisWorkoutId, nextWorkoutId,
      });
    })();
    return () => { cancelled = true; };
  }, [row, seq]);
  return { state: row === null ? { status: "loading" } : state, reload };
}
